/**
 * Application-level capture of Chromium's `beforeinstallprompt`.
 *
 * WHY THIS IS AT STARTUP AND NOT IN THE COMPONENT
 * Chromium fires `beforeinstallprompt` once per page load, when its own engagement criteria are
 * met — typically while the customer is still on the storefront root. The install card lives on
 * My Orders, which is lazy-loaded behind authentication, so a listener owned by that component is
 * registered long after the event has already fired and been discarded. The offer would then
 * never appear on Android except on a cold load of My Orders itself. Capturing here, alongside
 * the existing global handlers, is the smallest location that reliably covers the whole page
 * lifetime.
 *
 * SUPPRESSION CONTRACT — the reason eligibility is decided here rather than at render time
 * Calling `preventDefault()` is what suppresses Chromium's own install affordance for the page,
 * and it is also what makes the event retainable for a later `prompt()`. Those two effects cannot
 * be separated, so the decision has to be made in the handler itself. It is therefore made
 * against everything already knowable at that moment:
 *
 *   - the platform can install at all (mobile Chromium, not desktop, not iOS)
 *   - the app is not already running installed
 *   - the frequency policy is not in its cooldown
 *
 * A customer failing any of those is left completely alone: no `preventDefault()`, so Chromium's
 * own install UI behaves exactly as it would if this code did not exist. In particular a
 * frequency-blocked customer keeps the browser's native install path rather than losing both.
 *
 * The one case this cannot foresee is an eligible customer who never opens My Orders during the
 * page load. Their mini-infobar is suppressed and no card is shown — but Chromium's menu entry
 * ("Install app" / "Add to Home screen") is never suppressed by `preventDefault()`, so an
 * installation path always remains.
 *
 * Holds no React state and pulls in no state-management library: one module-level slot plus a
 * subscriber set, which is all a single retained event needs.
 */

import {
  isStandaloneDisplayMode,
  resolveInstallPlatform,
} from "@/domain/install/install-capability";
import { canShowInstallPrompt } from "@/domain/install/install-prompt";

/**
 * `beforeinstallprompt` is not in the TypeScript DOM library — it is a Chromium extension to the
 * platform.
 */
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type Subscriber = (event: BeforeInstallPromptEvent | null) => void;

let installed = false;
let retained: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<Subscriber>();

/** A failing consumer must never break capture for the others, or for the caller. */
function emit(subscriber: Subscriber): void {
  try {
    subscriber(retained);
  } catch {
    // swallowed by design
  }
}

function notify(): void {
  for (const subscriber of subscribers) {
    emit(subscriber);
  }
}

/** Whether presenting our own install UI is possible at all for this page load. */
function isInstallOfferEligible(): boolean {
  try {
    if (isStandaloneDisplayMode()) return false;
    if (resolveInstallPlatform() !== "chromium") return false;
    return canShowInstallPrompt();
  } catch {
    // Never suppress the browser's affordance on the strength of a failed check.
    return false;
  }
}

function onBeforeInstallPrompt(event: Event): void {
  try {
    // Decide BEFORE suppressing. See the suppression contract above.
    if (!isInstallOfferEligible()) return;

    event.preventDefault();
    retained = event as BeforeInstallPromptEvent;
    notify();
  } catch {
    // never throw from handler
  }
}

function onAppInstalled(): void {
  try {
    retained = null;
    notify();
  } catch {
    // never throw from handler
  }
}

/** The retained event, or null when none was captured. */
export function getRetainedInstallPrompt(): BeforeInstallPromptEvent | null {
  return retained;
}

/**
 * Releases the retained event. `beforeinstallprompt` is single-use, so the consumer clears it as
 * soon as it calls `prompt()`.
 */
export function clearRetainedInstallPrompt(): void {
  if (!retained) return;
  retained = null;
  notify();
}

/**
 * Observes arrival or release of the retained event. Returns an unsubscribe function; the
 * subscriber is called immediately with the current value so a late consumer sees an event that
 * was captured before it mounted.
 */
export function subscribeToInstallPrompt(subscriber: Subscriber): () => void {
  subscribers.add(subscriber);
  emit(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

/** Register the page-lifetime install-event listeners. Idempotent. */
export function installBeforeInstallPromptCapture(): void {
  if (typeof window === "undefined") return;
  if (installed) return;
  installed = true;

  window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  window.addEventListener("appinstalled", onAppInstalled);
}

/** Test-only reset for Jest isolation. */
export function resetInstallPromptCaptureForTests(): void {
  if (typeof window !== "undefined" && installed) {
    window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", onAppInstalled);
  }
  installed = false;
  retained = null;
  subscribers.clear();
}
